import posthog from "posthog-js";
import {
  PostHogProvider as PHProvider,
  usePostHog as usePHPostHog,
} from "posthog-js/react";
import React from "react";
import {
  applyPostHogSessionPolicy,
  isPostHogDisabledPath,
} from "@/lib/posthogPathGuard";

interface PostHogProviderProps {
  children: React.ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({
  children,
}) => {
  // PostHog is initialized in main.tsx. Re-assert session recording once React mounts
  // (covers /decide race where recorder is not ready at init time).
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!posthog?.__loaded) return;
    const path = window.location.pathname || "/";
    if (isPostHogDisabledPath(path)) return;
    try {
      applyPostHogSessionPolicy(path, null);
    } catch {
      /* ignore */
    }
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
};

// Status hook (different shape from the official usePostHog — kept for useCombinedTracking compat)
export const usePostHog = () => {
  const [status, setStatus] = React.useState({
    isInitialized: false,
    isRecording: false,
    distinctId: null as string | null,
    sessionId: null as string | null,
  });

  React.useEffect(() => {
    const checkStatus = () => {
      if (posthog.__loaded) {
        setStatus({
          isInitialized: true,
          isRecording: posthog.sessionRecordingStarted?.() || false,
          distinctId: posthog.get_distinct_id?.() || null,
          sessionId: posthog.get_session_id?.() || null,
        });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  return {
    ...status,
    startRecording: () => {
      try {
        (posthog.startSessionRecording as ((force?: boolean) => void) | undefined)?.(
          true,
        );
      } catch {
        /* ignore */
      }
    },
    stopRecording: () => posthog.stopSessionRecording?.(),
  };
};

// Official PostHog instance hook (React context — works after PostHogProvider mounts)
export { usePHPostHog as usePostHogInstance };

export const usePostHogFeatureFlag = (flag: string) => {
  const ph = usePHPostHog();
  try {
    return ph?.getFeatureFlag?.(flag) ?? false;
  } catch {
    return false;
  }
};

export const useFeatureFlagEnabled = (flag: string): boolean | undefined => {
  const ph = usePHPostHog();
  try {
    const value = ph?.getFeatureFlag?.(flag);
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value !== "control" && value !== "false";
    return undefined;
  } catch {
    return undefined;
  }
};

export const usePostHogTracking = () => {
  const ph = usePHPostHog();

  return {
    trackTemplateUsage: (template: string, data?: Record<string, unknown>) =>
      ph?.capture("template_usage", { template, ...data }),
    trackButtonClick: (buttonId: string, data?: Record<string, unknown>) =>
      ph?.capture("button_click", { button_id: buttonId, ...data }),
    trackSocialClick: (platform: string, data?: Record<string, unknown>) =>
      ph?.capture("social_click", { platform, ...data }),
    trackConversion: (type: string, data?: Record<string, unknown>) =>
      ph?.capture("conversion", { conversion_type: type, ...data }),
    trackEnhancedButtonClick: (buttonData: Record<string, unknown>) =>
      ph?.capture("enhanced_button_click", buttonData),
  };
};

export const withPostHogTracking = (Component: React.ComponentType) => Component;

export default PostHogProvider;
